import React, { useState, useMemo, useEffect } from 'react'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix-fetcher'
import Fetcher from '@lib/fetcher'
import BasketUpdateEvent from '@lib/basket-update-event'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import InstanceBasket from '../instance/basket'
import EnhancedPriorityBookingHandler from '../priority-booking/enhanced-handler'

const EventBooking = props => {
  // Handle both direct props and remount attribute props
  const eventData = props.eventData || props['event-data']
  const instanceData = props.instanceData || props['instance-data']
  const [planData, setPlanData] = useState(null)

  // Parse the JSON data from attributes
  const event = useMemo(() => {
    try {
      return typeof eventData === 'string' ? JSON.parse(eventData) : eventData
    } catch (e) {
      console.error('Failed to parse event data:', e)
      return {}
    }
  }, [eventData])

  const instance = useMemo(() => {
    try {
      return typeof instanceData === 'string'
        ? JSON.parse(instanceData)
        : instanceData
    } catch (e) {
      console.error('Failed to parse instance data:', e)
      return {}
    }
  }, [instanceData])

  // Fetch basket contents
  const basketUrl = `/basket`
  const { data: basketContents, mutate } = useSWR(basketUrl, SpektrixFetcher)

  // Listen for basket update events
  useEffect(() => {
    const handleBasketUpdate = () => {
      mutate()
    }
    window.addEventListener(BasketUpdateEvent, handleBasketUpdate)
    return () => {
      window.removeEventListener(BasketUpdateEvent, handleBasketUpdate)
    }
  }, [mutate])

  // Fetch plan data to determine seating type
  const { data: plan } = useSWR(
    instance.id
      ? `/instances/${instance.spektrix_v3_id || instance.id}/plan`
      : null,
    Fetcher
  )

  useEffect(() => {
    if (plan) {
      setPlanData(plan)
    }
  }, [plan])

  // Determine if this is reserved seating
  const isReservedSeating = useMemo(() => {
    if (!planData) return false

    // Check if plan type is Reserved
    if (planData.type === 'Reserved') return true

    // Check if plan type is Group and has areas
    if (planData.type === 'Group' && planData.areas?.length > 0) {
      return true
    }

    // Check if plan has any seats defined
    if (planData.seats && Object.keys(planData.seats).length > 0) {
      return true
    }

    return false
  }, [planData])

  // Filter basket for this instance
  const inBasket = useMemo(() => {
    if (!basketContents?.tickets || !instance.spektrix_v3_id) return []

    return basketContents.tickets.filter(
      t =>
        t.instance.id === instance.spektrix_v3_id ||
        t.instance.id === instance.id
    )
  }, [basketContents, instance])

  // Format date safely
  const formatDate = () => {
    if (!instance.start) return 'Date not available'

    try {
      return new Date(instance.start).toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (e) {
      console.error('Date formatting error:', e)
      return instance.start
    }
  }

  return (
    <div className='p-2 md:p-6'>
      {/* Back link */}
      <div className='max-w-6xl mx-auto mb-4'>
        <a
          href={event.slug ? `/events/${event.slug}` : '/events'}
          className='inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors'
        >
          <ArrowLeftIcon className='w-4 h-4 mr-1' />
          <span>Back to event</span>
        </a>
      </div>

      {/* Conditional layout based on seating type */}
      <div className='max-w-6xl mx-auto'>
        {isReservedSeating ? (
          // Full width layout for reserved seating
          <div>
            {/* Event info - full width */}
            <div className='mb-6'>
              <h1 className='text-xl md:text-2xl font-bold mb-2 text-black dark:text-white'>
                {event.name || 'Loading...'}
              </h1>
              <p className='text-base md:text-lg text-black dark:text-white'>
                {formatDate()}
              </p>
              {instance.venue && (
                <p className='text-gray-600 dark:text-gray-400'>
                  {instance.venue.name}
                </p>
              )}
              {instance.parent_and_baby && (
                <div className='mt-4 p-4 bg-family/20 dark:bg-family/30 rounded-lg border border-family/30 dark:border-family/40'>
                  <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                    This performance ({formatDate()}) is exclusively for parents with children under 2 years. Unaccompanied adults are not admitted.
                  </p>
                </div>
              )}
            </div>

            {/* Ticket Selection - full width */}
            <div className='bg-gray-100 dark:bg-gray-800 rounded-lg event-booking-container w-full'>
              {event.id && instance.id ? (
                <EnhancedPriorityBookingHandler event={event} instance={instance}>
                  <InstanceBasket
                    instance={instance}
                    event={event}
                    onClose={() => window.history.back()}
                    inBasket={inBasket}
                    mutate={mutate}
                  />
                </EnhancedPriorityBookingHandler>
              ) : (
                <div className='text-center py-8'>
                  <p className='text-gray-600 dark:text-gray-400'>
                    Loading ticket selection...
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Side by side layout for unreserved seating
          <div className='lg:grid lg:grid-cols-2 lg:gap-8'>
            {/* Event info - left side on desktop */}
            <div className='mb-6 lg:mb-0'>
              <h1 className='text-xl md:text-2xl font-bold mb-2 text-black dark:text-white'>
                {event.name || 'Loading...'}
              </h1>
              <p className='text-base md:text-lg text-black dark:text-white'>
                {formatDate()}
              </p>
              {instance.venue && (
                <p className='text-gray-600 dark:text-gray-400'>
                  {instance.venue.name}
                </p>
              )}
              {instance.parent_and_baby && (
                <div className='mt-4 p-4 bg-family/20 dark:bg-family/30 rounded-lg border border-family/30 dark:border-family/40'>
                  <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                    This performance ({formatDate()}) is exclusively for parents with children under 2 years. Unaccompanied adults are not admitted.
                  </p>
                </div>
              )}
            </div>

            {/* Ticket Selection - right side on desktop */}
            <div>
              <div className='bg-gray-100 dark:bg-gray-800 rounded-lg event-booking-container'>
                {event.id && instance.id ? (
                  <EnhancedPriorityBookingHandler event={event} instance={instance}>
                    <InstanceBasket
                      instance={instance}
                      event={event}
                      onClose={() => window.history.back()}
                      inBasket={inBasket}
                      mutate={mutate}
                    />
                  </EnhancedPriorityBookingHandler>
                ) : (
                  <div className='text-center py-8'>
                    <p className='text-gray-600 dark:text-gray-400'>
                      Loading ticket selection...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EventBooking
